import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { UserStatus } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { SettingsContext } from "../Context";
import { getManagementStrategy } from "../helpers";
import UsersEmpty from "./Empty";
import { GET_MANAGED_USERS } from "./gql";
import { getSortedUsers } from "./helpers";
import { showUsersInfoDrawerNew } from "./InfoDrawerNew";
import { showUsersInviteDrawerNew } from "./InviteDrawerNew";
import UsersListNew from "./ListNew";
import { UsersListUser } from "./ListNew/types";
import UsersPageLayout from "./PageLayout";

type GetManagedUsersGql = {
  managedUsers: UsersListUser[];
};

const Users = () => {
  const { activationStatus } = useTypedContext(SettingsContext);
  const { accountName } = useTypedContext(AccountContext);
  const { loading, data, error } = useQuery<GetManagedUsersGql>(GET_MANAGED_USERS);

  useTitle(`Organization Settings · Users · ${accountName}`);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationUsers,
  });

  const managedUsers = data?.managedUsers;

  const activeUsersCount = useMemo(() => {
    if (managedUsers) {
      return managedUsers.filter((item) => item.status === UserStatus.Active).length;
    }

    return 0;
  }, [managedUsers]);

  const usersCount = managedUsers?.length || 0;

  const memoizedDisplayableUsers = useMemo<UsersListUser[]>(() => {
    if (managedUsers) {
      return getSortedUsers(managedUsers);
    }
    return [];
  }, [managedUsers]);

  const openInfoDrawer = () => {
    trackSegmentAnalyticsEvent("Info Click", {
      managementStrategy: getManagementStrategy(activationStatus),
    });

    showUsersInfoDrawerNew();
  };

  const openInviteDrawer = () => {
    trackSegmentAnalyticsEvent("Invite More Users click", {
      managementStrategy: getManagementStrategy(activationStatus),
      usersCount,
      activeUsersCount,
    });
    showUsersInviteDrawerNew({
      activationStatus,
      usersCount,
      activeUsersCount,
    });
  };

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.managedUsers) {
    return <PageLoading />;
  }

  if (!data?.managedUsers) {
    return <NotFoundPage />;
  }

  return (
    <UsersPageLayout
      openInviteDrawer={openInviteDrawer}
      openInfoDrawer={openInfoDrawer}
      hasActiveSearch={usersCount > 0}
    >
      {memoizedDisplayableUsers.length === 0 && (
        <Box align="center" justify="center" grow="1">
          <UsersEmpty />
        </Box>
      )}

      {memoizedDisplayableUsers.length > 0 && (
        <>
          <UsersListNew users={memoizedDisplayableUsers} />
        </>
      )}
    </UsersPageLayout>
  );
};

export default Users;
